/**
 * Route Configuration
 *
 * Central route definitions for the entire application.
 * Routes are organized by category: public, auth, and protected dashboard.
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  ABOUT: '/about',
  PRICING: '/pricing',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  CONTACT: '/contact',

  // Shared (public) routes
  SHARED_REPORT: '/shared/reports/:shareToken',

  // Auth routes (public-only when not authenticated)
  SIGN_IN: '/signin',
  SIGN_UP: '/signup',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password/:token',
  VERIFY_EMAIL: '/auth/verify-email/:token',
  OAUTH_CALLBACK: '/auth/oauth-callback',
  MFA_CHALLENGE: '/auth/mfa-challenge',

  // Invitation routes (accessible to all — auth state handled in page)
  INVITATION_ACCEPT: '/invitations/:token',

  // Protected dashboard routes
  DASHBOARD: '/dashboard',
  DASHBOARD_PROFILE: '/dashboard/profile',
  DASHBOARD_PROFILE_GENERAL: '/dashboard/profile',
  DASHBOARD_PROFILE_SECURITY: '/dashboard/profile/security',
  DASHBOARD_PROFILE_PREFERENCES: '/dashboard/profile/preferences',
  DASHBOARD_PROFILE_SESSIONS: '/dashboard/profile/sessions',
  DASHBOARD_PROFILE_DATA_PRIVACY: '/dashboard/profile/data-privacy',
  DASHBOARD_PROFILE_NOTIFICATIONS: '/dashboard/profile/notifications',
  DASHBOARD_WORKSPACES: '/dashboard/workspaces',
  DASHBOARD_CHANNELS: '/dashboard/channels',
  DASHBOARD_POSTS: '/dashboard/posts',
  DASHBOARD_CALENDAR: '/dashboard/calendar',
  DASHBOARD_MEDIA: '/dashboard/media',
  DASHBOARD_PUBLISHING: '/dashboard/publishing',
  DASHBOARD_CAMPAIGNS: '/dashboard/campaigns',
  DASHBOARD_CAMPAIGNS_CREATE: '/dashboard/campaigns/create',
  DASHBOARD_CAMPAIGNS_NEW: '/dashboard/campaigns/new',
  DASHBOARD_CAMPAIGNS_AI_NEW: '/dashboard/campaigns/ai-new',
  DASHBOARD_CAMPAIGN_DETAIL: '/dashboard/campaigns/:campaignId',
  DASHBOARD_CAMPAIGN_EDIT: '/dashboard/campaigns/:campaignId/edit',
  DASHBOARD_CAMPAIGN_COMPARE: '/dashboard/campaigns/compare',
  DASHBOARD_CONTENT: '/dashboard/content',
  DASHBOARD_CONTENT_NEW: '/dashboard/content/new',
  DASHBOARD_CONTENT_DETAIL: '/dashboard/content/:postId',
  DASHBOARD_CONTENT_APPROVAL_QUEUE: '/dashboard/content/approval-queue',
  DASHBOARD_INBOX: '/dashboard/inbox',
  DASHBOARD_NOTIFICATIONS: '/dashboard/notifications',
  DASHBOARD_TRENDS: '/dashboard/trends',
  DASHBOARD_TREND_DETAIL: '/dashboard/trends/:trendId',
  DASHBOARD_TREND_HISTORY: '/dashboard/trends/history',
  DASHBOARD_CRISIS_EVENTS: '/dashboard/trends/crisis',
  DASHBOARD_CRISIS_DETAIL: '/dashboard/trends/crisis/:crisisEventId',
  DASHBOARD_PLAYBOOKS: '/dashboard/trends/playbooks',
  DASHBOARD_PLAYBOOK_NEW: '/dashboard/trends/playbooks/new',
  DASHBOARD_PLAYBOOK_EDIT: '/dashboard/trends/playbooks/:playbookId/edit',
  DASHBOARD_MENTIONS: '/dashboard/mentions',
  DASHBOARD_ANALYTICS: '/dashboard/analytics',
  DASHBOARD_REPORTS: '/dashboard/reports',
  DASHBOARD_REPORT_NEW: '/dashboard/reports/new',
  DASHBOARD_REPORT_EDIT: '/dashboard/reports/:reportId/edit',
  DASHBOARD_REPORT_VIEW: '/dashboard/reports/:reportId/view',
  DASHBOARD_KNOWLEDGE: '/dashboard/knowledge',
  DASHBOARD_KNOWLEDGE_DETAIL: '/dashboard/knowledge/:id',
  DASHBOARD_BRAND_VOICE: '/dashboard/brand-voice',
  DASHBOARD_BRAND_VOICE_NEW: '/dashboard/brand-voice/new',
  DASHBOARD_BRAND_VOICE_EDIT: '/dashboard/brand-voice/:id/edit',
  DASHBOARD_PERSONAS: '/dashboard/personas',
  DASHBOARD_PERSONA_DETAIL: '/dashboard/personas/:personaId',
  DASHBOARD_PERSONAS_NEW: '/dashboard/personas/new',
  DASHBOARD_PERSONAS_EDIT: '/dashboard/personas/:personaId/edit',
  DASHBOARD_AI_AGENTS: '/dashboard/ai-agents',
  DASHBOARD_AI_AGENTS_LOGS: '/dashboard/ai-agents/logs',
  DASHBOARD_AI_AGENTS_CONFIG: '/dashboard/ai-agents/:agentType',
  DASHBOARD_SETTINGS: '/dashboard/settings',
  DASHBOARD_SETTINGS_TEAM: '/dashboard/settings/team',
  DASHBOARD_SETTINGS_SECURITY: '/dashboard/settings/security',
  DASHBOARD_SETTINGS_BILLING: '/dashboard/settings/billing',
  DASHBOARD_ADMIN: '/dashboard/admin',
  DASHBOARD_ADMIN_AUDIT_LOGS: '/dashboard/admin',
  DASHBOARD_ADMIN_APPROVAL_WORKFLOWS: '/dashboard/admin/approval-workflows',
  DASHBOARD_ADMIN_APPROVAL_WORKFLOW_NEW: '/dashboard/admin/approval-workflows/new',
  DASHBOARD_ADMIN_APPROVAL_WORKFLOW_EDIT: '/dashboard/admin/approval-workflows/:workflowId/edit',
  DASHBOARD_ADMIN_SECURITY: '/dashboard/admin/security',
  DASHBOARD_ADMIN_DATA_GOVERNANCE: '/dashboard/admin/data-governance',
  DASHBOARD_ADMIN_BILLING: '/dashboard/admin/billing',
  DASHBOARD_ADMIN_DEVELOPER: '/dashboard/admin/developer',
  DASHBOARD_ADMIN_API_KEYS: '/dashboard/admin/api-keys',
  DASHBOARD_ADMIN_WEBHOOKS: '/dashboard/admin/webhooks',

  // Ad Manager routes
  DASHBOARD_AD_MANAGER: '/dashboard/ad-manager',
  DASHBOARD_AD_MANAGER_ACCOUNTS: '/dashboard/ad-manager/accounts',
  DASHBOARD_AD_MANAGER_ACCOUNT_DETAIL: '/dashboard/ad-manager/accounts/:adAccountId',
  DASHBOARD_AD_MANAGER_CAMPAIGNS: '/dashboard/ad-manager/campaigns',
  DASHBOARD_AD_MANAGER_CAMPAIGN_CREATE: '/dashboard/ad-manager/campaigns/create',
  DASHBOARD_AD_MANAGER_CAMPAIGN_DETAIL: '/dashboard/ad-manager/campaigns/:id',
  DASHBOARD_AD_MANAGER_CAMPAIGN_EDIT: '/dashboard/ad-manager/campaigns/:id/edit',
  DASHBOARD_AD_MANAGER_AD_SET_CREATE: '/dashboard/ad-manager/ad-sets/create',
  DASHBOARD_AD_MANAGER_AD_SET_DETAIL: '/dashboard/ad-manager/ad-sets/:adSetId',
  DASHBOARD_AD_MANAGER_AD_CREATE: '/dashboard/ad-manager/ads/create',
  DASHBOARD_AD_MANAGER_AD_DETAIL: '/dashboard/ad-manager/ads/:id',
  DASHBOARD_AD_MANAGER_CREATIVES: '/dashboard/ad-manager/creatives',
  DASHBOARD_AD_MANAGER_CREATIVE_CREATE: '/dashboard/ad-manager/creatives/create',
  DASHBOARD_AD_MANAGER_CREATIVE_DETAIL: '/dashboard/ad-manager/creatives/:creativeId',
  DASHBOARD_AD_MANAGER_AUDIENCES: '/dashboard/ad-manager/audiences',
  DASHBOARD_AD_MANAGER_AUDIENCE_CREATE: '/dashboard/ad-manager/audiences/create',
  DASHBOARD_AD_MANAGER_INSIGHTS: '/dashboard/ad-manager/insights',
  DASHBOARD_AD_MANAGER_CROSS_CAMPAIGN_INSIGHTS: '/dashboard/ad-manager/cross-campaign-insights',
  DASHBOARD_AD_MANAGER_BUDGET_TOOL: '/dashboard/ad-manager/budget-tool',

  // AI Ad Execution routes
  DASHBOARD_AD_MANAGER_AI_CAMPAIGN: '/dashboard/ad-manager/ai-campaign',
  DASHBOARD_AD_MANAGER_EXECUTIONS: '/dashboard/ad-manager/executions',
  DASHBOARD_AD_MANAGER_EXECUTION_DETAIL: '/dashboard/ad-manager/executions/:executionId',
  DASHBOARD_AD_MANAGER_EXECUTION_REVIEW: '/dashboard/ad-manager/executions/:executionId/review',
  DASHBOARD_AD_MANAGER_EXECUTION_PERFORMANCE: '/dashboard/ad-manager/executions/:executionId/performance',
  DASHBOARD_AD_MANAGER_EXECUTION_RE_EXECUTE: '/dashboard/ad-manager/executions/:executionId/re-execute',
  DASHBOARD_AD_MANAGER_EXECUTION_MONITOR: '/dashboard/ad-manager/execution-monitor',

  // AI Ad Wizard (100% AI-powered ad campaign creation)
  DASHBOARD_AD_MANAGER_AI_WIZARD: '/dashboard/ad-manager/ai-wizard',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Helper to generate dynamic route paths
 */
export const buildRoute = {
  resetPassword: (token: string) => `/reset-password/${token}`,
  verifyEmail: (token: string) => `/auth/verify-email/${token}`,
  invitationAccept: (token: string) => `/invitations/${token}`,
  knowledgeDocumentDetail: (id: string) => `/dashboard/knowledge/${id}`,
  brandVoiceEdit: (id: string) => `/dashboard/brand-voice/${id}/edit`,
  personaEdit: (personaId: string) => `/dashboard/personas/${personaId}/edit`,
  personaDetail: (personaId: string) => `/dashboard/personas/${personaId}`,
  agentConfig: (agentType: string) => `/dashboard/ai-agents/${agentType}`,
  agentLogs: () => `/dashboard/ai-agents/logs`,
  campaignCreate: () => `/dashboard/campaigns/create`,
  campaignNew: () => `/dashboard/campaigns/new`,
  campaignAINew: () => `/dashboard/campaigns/ai-new`,
  campaignDetail: (campaignId: string) => `/dashboard/campaigns/${campaignId}`,
  campaignEdit: (campaignId: string) => `/dashboard/campaigns/${campaignId}/edit`,
  campaignCompare: () => `/dashboard/campaigns/compare`,
  contentNew: () => `/dashboard/content/new`,
  contentDetail: (postId: string) => `/dashboard/content/${postId}`,
  contentPostDetail: (postId: string) => `/dashboard/content/${postId}`,
  contentApprovalQueue: () => `/dashboard/content/approval-queue`,
  publishing: () => `/dashboard/publishing`,
  notifications: () => `/dashboard/notifications`,
  profileNotifications: () => `/dashboard/profile/notifications`,
  settingsSecurity: () => `/dashboard/settings/security`,
  settingsBilling: () => `/dashboard/settings/billing`,
  trends: () => `/dashboard/trends`,
  trendDetail: (trendId: string) => `/dashboard/trends/${trendId}`,
  trendHistory: () => `/dashboard/trends/history`,
  crisisEvents: () => `/dashboard/trends/crisis`,
  crisisDetail: (crisisEventId: string) => `/dashboard/trends/crisis/${crisisEventId}`,
  playbooks: () => `/dashboard/trends/playbooks`,
  playbookNew: () => `/dashboard/trends/playbooks/new`,
  playbookEdit: (playbookId: string) => `/dashboard/trends/playbooks/${playbookId}/edit`,
  reports: () => `/dashboard/reports`,
  reportNew: () => `/dashboard/reports/new`,
  reportEdit: (reportId: string) => `/dashboard/reports/${reportId}/edit`,
  reportView: (reportId: string) => `/dashboard/reports/${reportId}/view`,
  sharedReport: (shareToken: string) => `/shared/reports/${shareToken}`,
  admin: () => `/dashboard/admin`,
  approvalWorkflows: () => `/dashboard/admin/approval-workflows`,
  approvalWorkflowNew: () => `/dashboard/admin/approval-workflows/new`,
  approvalWorkflowEdit: (workflowId: string) =>
    `/dashboard/admin/approval-workflows/${workflowId}/edit`,
  adminApiKeys: () => `/dashboard/admin/api-keys`,
  adminWebhooks: () => `/dashboard/admin/webhooks`,

  // Ad Manager route builders
  adManager: () => `/dashboard/ad-manager`,
  adManagerAccounts: () => `/dashboard/ad-manager/accounts`,
  adManagerAccountDetail: (adAccountId: string) => `/dashboard/ad-manager/accounts/${adAccountId}`,
  adManagerCampaigns: () => `/dashboard/ad-manager/campaigns`,
  adManagerCampaignCreate: () => `/dashboard/ad-manager/campaigns/create`,
  adManagerCampaignDetail: (id: string) => `/dashboard/ad-manager/campaigns/${id}`,
  adManagerCampaignEdit: (id: string) => `/dashboard/ad-manager/campaigns/${id}/edit`,
  adManagerAdSetCreate: () => `/dashboard/ad-manager/ad-sets/create`,
  adManagerAdSetDetail: (adSetId: string) => `/dashboard/ad-manager/ad-sets/${adSetId}`,
  adManagerAdCreate: () => `/dashboard/ad-manager/ads/create`,
  adManagerAdDetail: (id: string) => `/dashboard/ad-manager/ads/${id}`,
  adManagerCreatives: () => `/dashboard/ad-manager/creatives`,
  adManagerCreativeCreate: () => `/dashboard/ad-manager/creatives/create`,
  adManagerCreativeDetail: (creativeId: string) => `/dashboard/ad-manager/creatives/${creativeId}`,
  adManagerAudiences: () => `/dashboard/ad-manager/audiences`,
  adManagerAudienceCreate: () => `/dashboard/ad-manager/audiences/create`,
  adManagerInsights: () => `/dashboard/ad-manager/insights`,
  adManagerCrossCampaignInsights: () => `/dashboard/ad-manager/cross-campaign-insights`,
  adManagerBudgetTool: () => `/dashboard/ad-manager/budget-tool`,

  // AI Ad Execution route builders
  adManagerAICampaign: () => `/dashboard/ad-manager/ai-campaign`,
  adManagerExecutions: () => `/dashboard/ad-manager/executions`,
  adManagerExecutionDetail: (executionId: string) =>
    `/dashboard/ad-manager/executions/${executionId}`,
  adManagerExecutionReview: (executionId: string) =>
    `/dashboard/ad-manager/executions/${executionId}/review`,
  adManagerExecutionPerformance: (executionId: string) =>
    `/dashboard/ad-manager/executions/${executionId}/performance`,
  adManagerExecutionReExecute: (executionId: string) =>
    `/dashboard/ad-manager/executions/${executionId}/re-execute`,
  adManagerExecutionMonitor: () => `/dashboard/ad-manager/execution-monitor`,

  // AI Ad Wizard route builder
  adManagerAIWizard: () => `/dashboard/ad-manager/ai-wizard`,
} as const;